/**
 * MontaTech v2 - PreviewCanvas Component
 *
 * Canvas de preview com barra de progresso real
 * Suporta status: collecting, calculating, applying
 */

import { ChevronLeft, ChevronRight, Loader2, LayoutGrid, Package, Wifi } from 'lucide-react';
import type { MontaTechCurveData, MontaTechStatus, RFigure } from '../types';
import type { NestingBoundsWarning } from '../services/nestingBoundsAudit';
import type { useCanvas } from '../hooks/useCanvas';

interface PreviewCanvasProps {
  containerRef: React.RefObject<HTMLDivElement>;
  canvas: ReturnType<typeof useCanvas>;
  status: MontaTechStatus;
  error: string | null;
  curveData: MontaTechCurveData | null;
  progress: number;
  progressLabel: string | null;
  isRecovering: boolean;
  emptyState: {
    title: string;
    description: string;
    actionLabel: string;
  } | null;
  nestingResult: RFigure[] | null;
  maximumY: number | null;
  boundsWarning: NestingBoundsWarning | null;
  onRetryCollect: () => void;
}

export function PreviewCanvas({
  containerRef,
  canvas,
  status,
  error,
  curveData,
  progress,
  progressLabel,
  isRecovering,
  emptyState,
  nestingResult,
  maximumY,
  boundsWarning,
  onRetryCollect
}: PreviewCanvasProps) {
  const {
    canvasRef,
    isDragging,
    pagination,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    nextPage,
    prevPage
  } = canvas;

  const showPagination = pagination.totalItems > 0;
  const isLoading = status === 'collecting' || status === 'calculating' || status === 'applying' || status === 'optimizing' || status === 'loading';
  const isNestingMode = nestingResult && nestingResult.length > 0;
  const hasPreviewContent = (!!curveData && curveData.count > 0) || !!isNestingMode;
  const totalObjects = curveData?.count ?? 0;
  const showCollectSkeleton = status === 'collecting' && !hasPreviewContent;
  const maximumYExceedsProfile =
    boundsWarning?.maximumYExcessMm != null && boundsWarning.maximumYExcessMm > 0;

  // Cor do progresso baseada no status
  const progressGradient = status === 'collecting'
    ? 'from-emerald-500 to-green-500'
    : status === 'applying'
      ? 'from-amber-500 to-orange-500'
      : status === 'optimizing'
        ? 'from-purple-500 to-pink-500'
        : 'from-blue-500 to-cyan-500';

  // Icone/cor do indicador
  const statusColor = status === 'collecting'
    ? 'text-emerald-400'
    : status === 'applying'
      ? 'text-amber-400'
      : status === 'optimizing'
        ? 'text-purple-400'
        : 'text-blue-400';

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Mode indicator bar */}
      {!isLoading && pagination.totalItems > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-navy-700 bg-navy-800/40">
          <div className="flex items-center gap-2">
            {isNestingMode ? (
              <div className="flex items-center gap-1.5 text-green-400">
                <Package className="w-4 h-4" />
                <span className="text-xs font-semibold">RESULTADO</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-blue-400">
                <LayoutGrid className="w-4 h-4" />
                <span className="text-xs font-semibold">OBJETOS</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-400">
            {isNestingMode ? (
              <>
                {maximumY != null && (
                  <span>
                    Altura:{' '}
                    <span className={maximumYExceedsProfile ? 'font-medium text-amber-300' : 'font-medium text-gray-200'}>
                      {maximumY.toFixed(1)} mm
                      {maximumYExceedsProfile && boundsWarning
                        ? ` (+${boundsWarning.maximumYExcessMm?.toFixed(1)} mm)`
                        : ''}
                    </span>
                  </span>
                )}
                <span>
                  Blocos: <span className="font-medium text-gray-200">{pagination.totalItems}</span>
                </span>
              </>
            ) : (
              <>
                <span>
                  Objetos unicos: <span className="text-gray-300">{pagination.totalItems}</span>
                </span>
                <span>
                  Total de objetos: <span className="text-gray-300">{totalObjects}</span>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Canvas area */}
      <div
        ref={containerRef}
        className={`flex-1 relative min-h-0 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <canvas ref={canvasRef} className="w-full h-full" />

        {/* Skeleton inicial da coleta automática */}
        {showCollectSkeleton && (
          <div className="absolute inset-0 bg-navy-900/95 flex flex-col items-center justify-center px-6">
            <div className="w-full max-w-[520px] space-y-4">
              <div className="space-y-2">
                <div className="h-4 w-40 rounded bg-navy-700/80 animate-pulse" />
                <div className="h-3 w-64 rounded bg-navy-800 animate-pulse" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="h-28 rounded-xl border border-navy-700 bg-navy-800/70 animate-pulse" />
                <div className="h-28 rounded-xl border border-navy-700 bg-navy-800/70 animate-pulse" />
                <div className="h-24 rounded-xl border border-navy-700 bg-navy-800/60 animate-pulse" />
                <div className="h-24 rounded-xl border border-navy-700 bg-navy-800/60 animate-pulse" />
              </div>
            </div>

            <span className="mt-5 text-sm text-gray-300 font-medium">
              {progressLabel || 'Coletando seleção atual...'}
            </span>
          </div>
        )}

        {/* Loading overlay with progress */}
        {isLoading && !showCollectSkeleton && (
          <div className="absolute inset-0 bg-navy-900/80 flex flex-col items-center justify-center">
            {/* Progress bar */}
            {progress > 0 ? (
              <div className="w-56 flex flex-col items-center gap-3">
                {status === 'calculating' && (
                  <div className="flex items-center gap-2 text-green-400">
                    <Wifi className="w-4 h-4" />
                    <span className="text-xs font-medium">Tempo Real</span>
                  </div>
                )}
                <div className="w-full bg-navy-700 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${progressGradient} transition-all duration-300 ease-out`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-lg font-bold text-white">{Math.round(progress)}%</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <Loader2 className={`w-12 h-12 ${statusColor} animate-spin`} />
                </div>
              </div>
            )}

            {/* Label principal */}
            <span className="mt-4 text-sm text-gray-300 font-medium">
              {progressLabel || (
                status === 'collecting' ? 'Coletando objetos...' :
                status === 'calculating' ? 'Calculando encaixe...' :
                status === 'optimizing' ? 'Otimizando resultado...' :
                status === 'applying' ? 'Aplicando no CorelDRAW...' :
                'Carregando...'
              )}
            </span>

            {/* Banner de recuperação de conexão zumbi (só durante o cálculo) */}
            {isRecovering && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-amber-900/70 border border-amber-700 px-3 py-1.5">
                <Loader2 className="w-3.5 h-3.5 text-amber-300 animate-spin" />
                <span className="text-[11px] text-amber-200 font-medium">Conexão instável, reconectando...</span>
              </div>
            )}
          </div>
        )}

        {emptyState && !isLoading && !error && (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="w-full max-w-md rounded-2xl border border-navy-700 bg-navy-800/85 p-6 text-center shadow-xl">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-navy-700">
                <LayoutGrid className="h-6 w-6 text-blue-300" />
              </div>
              <h3 className="text-base font-semibold text-gray-100">{emptyState.title}</h3>
              <p className="mt-2 text-sm text-gray-400">{emptyState.description}</p>
              <button
                onClick={onRetryCollect}
                className="mt-5 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                {emptyState.actionLabel}
              </button>
            </div>
          </div>
        )}

        {/* Error toast */}
        {error && !isLoading && (
          <div className="absolute bottom-2 left-2 right-2 bg-red-900/90 border border-red-800 rounded-lg px-3 py-2 max-h-24 overflow-y-auto">
            <div className="flex items-start gap-2">
              <span className="text-red-400 text-sm shrink-0">!</span>
              <div className="flex-1 min-w-0">
                <span className="text-xs text-red-200 font-medium">Erro</span>
                <p className="text-[10px] text-red-300 mt-0.5 break-words">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination bar */}
      {showPagination && !isLoading && (
        <div className="flex items-center justify-between px-3 py-1.5 border-t border-navy-700/70 bg-navy-900/80">
          <div className="flex items-center gap-1">
            <button
              onClick={prevPage}
              disabled={pagination.currentPage === 1}
              className="p-1 rounded hover:bg-navy-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-400" />
            </button>

            <span className="text-xs text-gray-300 min-w-[60px] text-center">
              <span className="font-medium">{pagination.currentPage}</span>
              <span className="text-gray-500"> / {pagination.totalPages}</span>
            </span>

            <button
              onClick={nextPage}
              disabled={pagination.currentPage === pagination.totalPages}
              className="p-1 rounded hover:bg-navy-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <span className="text-[10px] text-gray-500">
            {pagination.startIndex + 1}-{pagination.endIndex} de {pagination.totalItems}
          </span>
        </div>
      )}
    </div>
  );
}
